import React, { FC, useState } from 'react';
import {
	DeleteRedditReportDocument,
	RedditReportsDocument,
	ReportDateType,
	ToggleRedditReportStatusDocument,
	UserAccountLevel,
} from 'wb-reddit/graphql/generated';
import {
	Button,
	ButtonAppearance,
	ButtonColor,
	DataDownload,
	Dropdown,
	FileCopyIcon,
	isReddit, isSpotify,
	Label,
	LoaderBars,
	Menu,
	PauseIcon,
	PlayIcon,
	Trash,
	useOpen,
	MenuDotsIcon,
	NetworkConnectionIcon,
} from '@aleph/shared-components';
import dayjs from 'dayjs';
import useCreateRedditReport from 'wb-reddit/app/routes/reports/create/useCreateRedditReport';
import { useMutation } from '@apollo/client';
import { downloadRedditReport, getReportClientIds, ReportRow } from 'wb-reddit/app/routes/reports/utils';
import useReportActionsOpen from 'wb-reddit/app/routes/reports/hooks/useReportActionsOpen';
import { RedditReport } from 'wb-reddit/app/routes/reports/components/report';
import RedditReportSharingModal from 'wb-reddit/app/routes/reports/components/sharing';
import useUserMetaData from 'wb-reddit/app/common/hooks/useUserMetaData';
import { HandleUpdateRow } from 'wb-reddit/app/routes/hooks/useUpdateRows';
import { useTranslation } from 'react-i18next';
import ReportLinking from 'wb-reddit/app/routes/reports/components/actions/ReportLinking';
import { DeleteSpotifyReportDocument, SpotifyReportsDocument } from 'wb-spotify/graphql/generated';
import { LIFE_TIME_METRICS } from 'wb-spotify/app/routes/superGrid/statistics/utils';
import useSupergridSettings from 'wb-spotify/app/routes/superGrid/hooks/useSupergridSettings';
import { SpotifyReportSchedule, TIME_REGEX } from 'wb-spotify/app/common/utils';

interface IProps {
	report: RedditReport;
	handleUpdateRow: HandleUpdateRow<ReportRow>;
}

const RedditReportsActions: FC<IProps> = ({ report, handleUpdateRow }) => {
	const { t } = useTranslation();
	const { toggleLifetimeStats } = useSupergridSettings();

	const {
		setFilters, setName, setMetrics, setBreakdowns, setDates, setSchedule, setUserGroupIds, setUserIds, setTime, setPrivateUserGroupIds,
	} = useCreateRedditReport();

	const { setReportActionsOpenId } = useReportActionsOpen();
	const [downloading, setDownloading] = useState<boolean>(false);
	const platformUser = useUserMetaData();

	const [sharingOpen, { onOpen: onOpenSharing, onClose: onCloseSharing }] = useOpen(false);

	const [deleteRedditReport, { loading: deleting }] = useMutation(isReddit()
		? DeleteRedditReportDocument
		: DeleteSpotifyReportDocument, {
		refetchQueries:      [isReddit() ? RedditReportsDocument : SpotifyReportsDocument],
		awaitRefetchQueries: true,
	});
	const [toggleReportStatus, { loading: toggling }] = useMutation(ToggleRedditReportStatusDocument);

	const onDuplicateReport = () => {
		const clientIds = getReportClientIds(report.reportApi?.reportApiFiltersOptions);
		setReportActionsOpenId(undefined);

		if (isSpotify()) {
			// @ts-ignore TODO might not work if metric is not in the list
			const isLifetimeStats = LIFE_TIME_METRICS.some(metric => report.reportMetrics?.includes?.(metric))
				// @ts-ignore
				|| report.reportDimensions?.every(breakdown => breakdown !== 'date');

			if (isLifetimeStats) {
				toggleLifetimeStats();
			}

			setFilters({
				userGroupId: [{ value: clientIds }],
			});

			setName(`Duplicate of ${report.reportName}`);
			// @ts-ignore
			setBreakdowns((report.reportDimensions));
			// @ts-ignore
			setMetrics(report.reportMetrics);
			setUserGroupIds(clientIds ?? []);
			setUserIds(report.users?.map(user => user?.id ?? '') ?? []);
			// @ts-ignore
			setPrivateUserGroupIds(report.userGroupId ?? []);

			const dateRangeValues = report.reportDateRangeOptions?.dateRangeValue ?? [];

			if (dateRangeValues?.length > 0) {
				setDates({ from: dayjs(dateRangeValues?.[0]), to: dayjs(dateRangeValues?.[1]) });
			}

			setSchedule(report.reportSchedule);

			if (isSpotify() && TIME_REGEX.test(report.reportSchedule)) {
				setSchedule(SpotifyReportSchedule.OnlyOnce);
				setTime(report.reportSchedule);
			}

			return;
		}

		if (clientIds.length > 0) {
			setFilters({
				clientId: [{ value: clientIds }],
			});
			setName(`Duplicate of ${report.reportName}`);
			setBreakdowns(report.reportApi?.reportApiDimensions as string[] ?? []);
			setMetrics(report.reportApi?.reportApiMetrics as string[] ?? []);

			setUserIds(report.users?.map(user => user?.id ?? '') ?? []);
			setUserGroupIds(report.userGroups?.map(userGroup => userGroup.id) ?? []);

			const dateRangeValues = report?.reportDateRangeOptions?.dateRangeValue ?? [];

			if (dateRangeValues?.length > 0) {
				setDates({ from: dayjs(dateRangeValues?.[0]), to: dayjs(dateRangeValues?.[1]) });
			}

			setSchedule(report.reportDateType ?? ReportDateType.Custom);
		}
	};

	return (
		<div
			id={`report_${report.reportId}`}
			className="flex justify-center items-center gap-[12px] relative"
		>
			{/* TODO: not yet implemented on spotify */}
			{isReddit() && (
				<ReportLinking
					report={report}
				/>)
			}
			{sharingOpen && (
				<RedditReportSharingModal
					report={report}
					onClose={onCloseSharing}
				/>
			)}
			{platformUser?.userAccountLevel !== UserAccountLevel.ReadonlyUser
				&& <Button
					leftNode={<NetworkConnectionIcon strokeWidth={1.5} />}
					appearance={ButtonAppearance.GHOST}
					onClick={onOpenSharing}
					data-testid="share-report-button"
				/>
			}

			{
				/*
				<Button
					leftNode={<CalendarTimeIcon />}
					appearance={ButtonAppearance.GHOST}
				/>
				*/
			}
			{(downloading
				? (
					<LoaderBars
						className="animate-spin"
						width={16}
						height={16}
					/>
				)
				: (
					<Button
						leftNode={<DataDownload />}
						appearance={ButtonAppearance.GHOST}
						onClick={async () => {
							setDownloading(true);
							await downloadRedditReport(report);
							setDownloading(false);
						}}
						data-testid="download-report-button"
					/>
				)
			)}

			{platformUser?.userAccountLevel !== UserAccountLevel.ReadonlyUser
				&& <Dropdown
					onOpen={() => {
						setReportActionsOpenId(report.reportId ?? '');
					}}
					arrow={false}
					onClose={() => {
						setReportActionsOpenId(undefined);
					}}
					color={ButtonColor.WISE_BLUE}
					data-testid="dropdown-report-button"
					triggerControl={({ isOpen, closeDropdown, openDropdown }) => (
						<Button
							leftNode={<MenuDotsIcon />}
							appearance={ButtonAppearance.GHOST}
							onClick={isOpen ? closeDropdown : openDropdown}
							data-testid="share-report-button"
						/>
					)}
				>
					<Menu.Item onClick={onDuplicateReport}>
						<FileCopyIcon
							width={12}
							height={12}
						/>
						<Label>
							Duplicate
						</Label>
					</Menu.Item>
					{isReddit() && (
						<Menu.Item onClick={async () => {
							await toggleReportStatus({
								variables: {
									reportId:     report?.reportId ?? '',
									reportActive: !report.reportReady,
								},
							});
							handleUpdateRow({
								row: {
									...report,
									reportReady: !report.reportReady,
								},
							});
						}}
						>
							{
								toggling ? <LoaderBars
									className="animate-spin"
									width={12}
									height={12}
								/> : (report.reportReady ? <PauseIcon
									width={12}
									height={12}
								/> : <PlayIcon
									width={12}
									height={12}
								/>)
							}
							<Label>{report.reportReady ? t('sc: pause', 'Pause') : t('sc: activate', 'Activate')}</Label>
						</Menu.Item>
					)}
					<Menu.Item onClick={async () => {
						await deleteRedditReport({
							variables: { reportId: report?.reportId ?? '' },
						});
						setReportActionsOpenId(undefined);
						handleUpdateRow({
							row:      report,
							isDelete: true,
						});
					}}
					>
						{
							deleting ? <LoaderBars
								className="animate-spin"
								width={12}
								height={12}
							/> : <Trash
								width={12}
								height={12}
							/>
						}
						<Label>{t('sc: delete', 'Delete')}</Label>
					</Menu.Item>
				</Dropdown>
			}
		</div>
	);
};

export default RedditReportsActions;
